import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls } from 'ai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createLotusAuthHeadersOptional, getHostageChatUrl } from '@/shared/api/lotus/hostageChat';
import { emitDebugEvent } from '@/features/copilot-debug';
import { ANALYTICS_EVENTS, analytics, logger } from '@/shared/lib';

import { useChatInjectionBuilder } from './useChatInjectionBuilder';

export type CopilotComposerMode = 'copilot' | 'internalNotes';

export interface UseCopilotChatOptions {
  userId: string;
  threadId: string;
}

export function useCopilotChat(options: UseCopilotChatOptions) {
  const { registry, buildChatInjections } = useChatInjectionBuilder();
  const { userId, threadId } = options;

  useEffect(() => {
    logger.info('copilot.chat.hook.mount', { userId });
    return () => {
      logger.info('copilot.chat.hook.unmount', { userId });
    };
  }, [userId]);

  const [toolCallsUncollected, setToolCallsUncollected] = useState(false);

  const transport = useMemo(() => {
    const api = getHostageChatUrl(userId);

    return new DefaultChatTransport({
      api,
      headers: async () => {
        const headers = await createLotusAuthHeadersOptional();
        logger.debug('copilot.chat.transport.headers', {
          api,
          hasAuthorizationHeader: Object.prototype.hasOwnProperty.call(headers, 'Authorization'),
        });
        return headers;
      },
      body: () => {
        setToolCallsUncollected(false);
        const { injections, debug } = buildChatInjections();

        logger.info('copilot.chat.transport.request', {
          api,
          ...debug,
        });
        emitDebugEvent('ai.transport.request', `Transport request → ${api}`, {
          api,
          ...debug,
          injections,
        });

        return { injections };
      },
    });
  }, [buildChatInjections, userId]);

  const chatResult = useChat({
    id: threadId,
    transport,
    experimental_throttle: 50,
    sendAutomaticallyWhen: (ctx) => {
      const shouldSend =
        ctx.messages.length > 0 &&
        lastAssistantMessageIsCompleteWithToolCalls(ctx) &&
        toolCallsUncollected;
      if (shouldSend) {
        emitDebugEvent('ai.autoContinue', 'Auto-continue triggered', {
          messageCount: ctx.messages.length,
        });
      }
      return shouldSend;
    },
    onToolCall({ toolCall }) {
      // pydantic-ai streams tool-input-available for ALL tools (server-side
      // and external) without setting providerExecuted. Only process tools the
      // frontend has registered handlers for — server-side tools are handled
      // by the server's tool-output-available chunks.
      if (!registry.hasSpec(toolCall.toolName)) {
        logger.debug('copilot.chat.toolCall.skipped', {
          toolCallId: toolCall.toolCallId,
          tool: toolCall.toolName,
          reason: 'no frontend handler (server-side tool)',
        });
        emitDebugEvent('ai.toolCall.skipped', `Server-side tool: ${toolCall.toolName}`, {
          toolCallId: toolCall.toolCallId,
          toolName: toolCall.toolName,
          reason: 'no frontend handler (server-side tool)',
        });
        return;
      }

      setToolCallsUncollected(true);
      logger.info('copilot.chat.toolCall.received', {
        toolCallId: toolCall.toolCallId,
        tool: toolCall.toolName,
        inputType: typeof toolCall.input,
      });
      emitDebugEvent('ai.toolCall.received', `Tool call: ${toolCall.toolName}`, {
        toolCallId: toolCall.toolCallId,
        toolName: toolCall.toolName,
        input: toolCall.input,
      });
      void analytics.track({
        name: ANALYTICS_EVENTS.copilot.chatToolCallReceived,
        context: { actor: 'copilot' },
        properties: {
          toolCallId: toolCall.toolCallId,
          tool: toolCall.toolName,
          inputType: typeof toolCall.input,
        },
      });
      const originatingUserMessageId = chatResult.messages.findLast(
        (message) => message.role === 'user'
      )?.id;

      registry
        .handleToolCall(toolCall, chatResult.addToolOutput, { originatingUserMessageId })
        .catch((err) => {
          logger.error(
            'copilot.chat.toolCall.unhandledError',
            { toolCallId: toolCall.toolCallId, tool: toolCall.toolName },
            err instanceof Error ? err : new Error(String(err))
          );
          chatResult.addToolOutput({
            toolCallId: toolCall.toolCallId,
            tool: toolCall.toolName,
            state: 'output-error',
            errorText: `Tool execution failed: ${err instanceof Error ? err.message : String(err)}`,
          });
        });
    },
  });

  const { status, stop, sendMessage: sendMessageBase } = chatResult;

  type SendMessageArgs = Parameters<typeof chatResult.sendMessage>;
  const pendingSendArgsRef = useRef<SendMessageArgs | null>(null);
  useEffect(() => {
    const isBusy = status === 'submitted' || status === 'streaming';
    if (isBusy) {
      return;
    }

    const pendingArgs = pendingSendArgsRef.current;
    if (!pendingArgs) {
      return;
    }

    pendingSendArgsRef.current = null;
    void sendMessageBase(...pendingArgs);
  }, [sendMessageBase, status]);

  const sendMessage = useCallback<typeof chatResult.sendMessage>(
    async (...args) => {
      const isBusy = status === 'submitted' || status === 'streaming';
      if (isBusy) {
        pendingSendArgsRef.current = args;
        stop();
        return;
      }

      return sendMessageBase(...args);
    },
    [sendMessageBase, status, stop]
  );

  const lastStatusRef = useRef(chatResult.status);
  useEffect(() => {
    if (lastStatusRef.current === chatResult.status) {
      return;
    }
    const prev = lastStatusRef.current;
    lastStatusRef.current = chatResult.status;
    logger.info('copilot.chat.status', { status: chatResult.status });
    emitDebugEvent('ai.status', `${prev} → ${chatResult.status}`, {
      prev,
      next: chatResult.status,
    });
  }, [chatResult.status]);

  useEffect(() => {
    if (!chatResult.error) {
      return;
    }
    logger.error(
      'copilot.chat.error',
      {
        surface: 'handled',
        feature: 'copilot',
        operation: 'chat.stream',
        userId,
        status,
      },
      chatResult.error
    );
    emitDebugEvent('ai.error', `Chat error: ${chatResult.error.message}`, {
      name: chatResult.error.name,
      message: chatResult.error.message,
      stack: chatResult.error.stack,
      userId,
      status,
    });
  }, [chatResult.error, status, userId]);

  return { ...chatResult, sendMessage, setMessages: chatResult.setMessages };
}
